// components/Sections/Admin/Matriculas/CargaMasiva.jsx
//
// Subir la base de matriculados de un recinto (ej. la del gimnasio Ñuñoa Plaza)
// desde un Excel, en vez de registrarlos uno a uno.
//
// Siempre en dos tiempos: primero el servidor valida el archivo y muestra fila por
// fila qué va a pasar (vista previa, no graba nada); recién al confirmar se crean.
// Quien ya tiene matrícula vigente en el recinto no se duplica.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Modal, Select, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FiCheckCircle, FiDownload, FiUploadCloud, FiAlertTriangle, FiSkipForward, FiUsers } from 'react-icons/fi';
import dayjs from 'dayjs';
import MatriculaService from '../../../../service/Matriculas';
import { useNotify } from '../../../../providers/NotificationProvider';
import { formatearNombre } from '../../../../utils/texto';
import { descargarPlantilla, leerPlanilla } from '../../../../utils/plantillaExcel';
import { EncabezadoAdmin, TarjetaResumen, ChipNeutro, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';

const mensaje = (e, porDefecto) => e?.error || e?.message || porDefecto;

const COLUMNAS = [
  { header: 'RUT', key: 'rut', requerida: true, ancho: 16, ejemplo: '12.345.678-5' },
  { header: 'Nombre', key: 'nombre', requerida: true, ancho: 32, ejemplo: 'María Pérez Soto' },
  { header: 'Email', key: 'email', ancho: 30, ejemplo: 'maria@correo.cl', alias: ['correo'] },
  { header: 'Teléfono', key: 'telefono', ancho: 16, ejemplo: '+56912345678', alias: ['fono', 'celular'] },
  { header: 'Fecha matrícula', key: 'fechaMatricula', ancho: 18, ejemplo: '15/03/2026', alias: ['fecha', 'fechainicio'],
    nota: 'dd/mm/aaaa. Desde ahí corre la vigencia. Vacío = hoy.' },
  { header: 'Vigencia', key: 'vigencia', ancho: 14, ejemplo: '1', nota: '1, 2 o 3 (años) o "por vida". Vacío = la del recinto.' },
  { header: 'Tipo de cliente', key: 'tipoPrecio', ancho: 18, ejemplo: 'Vecino', alias: ['tipo', 'tramo'],
    nota: 'Opcional: No vecino, Vecino, Adulto mayor o Funcionario. Solo informativo.' },
  { header: 'Última compra', key: 'ultimaCompra', ancho: 16, ejemplo: '', alias: ['ultimacompra'],
    nota: 'Opcional, dd/mm/aaaa. Desde ahí cuentan los 2 años sin compras para la caducidad. Vacío = hoy.' },
];

const INSTRUCCIONES = [
  'Carga masiva de matrículas',
  '',
  '1. Completa la hoja "Datos": una fila por persona. La fila gris es un ejemplo, bórrala.',
  '2. RUT y Nombre son obligatorios. El RUT se valida con su dígito verificador.',
  '3. Fecha matrícula: dd/mm/aaaa. Si se deja vacía se usa la fecha de la carga.',
  '4. Vigencia: 1, 2 o 3 (años) o "por vida". Si se deja vacía se usa la del recinto.',
  '   Una matrícula cuyo plazo ya se cumplió se registra como vencida (queda la historia, pero no da acceso).',
  '5. Última compra (opcional): si no se informa, los 2 años sin compras que hacen caducar la matrícula',
  '   se cuentan desde el día de la carga.',
  '6. Quien ya tiene matrícula vigente en el recinto se omite (no se duplica).',
  '7. Guarda como .xlsx (o .csv) y súbelo en el panel: primero verás la vista previa y después confirmas.',
];

const ESTADO_FILA = {
  ok: { label: 'Se registra', clase: 'bg-green-50 text-green-700' },
  omitida: { label: 'Se omite', clase: 'bg-gray-100 text-gray-600' },
  error: { label: 'Con error', clase: 'bg-red-50 text-red-700' },
};

const CargaMasiva = () => {
  const notify = useNotify();
  const inputRef = useRef(null);

  const [configs, setConfigs] = useState([]);
  const [recintoId, setRecintoId] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [filasArchivo, setFilasArchivo] = useState([]);
  const [vista, setVista] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [filtro, setFiltro] = useState('todas');

  useEffect(() => {
    MatriculaService.ListarMatriculas({})
      .then(r => setConfigs((r.data || []).filter(c => c.recinto)))
      .catch(e => notify.error(mensaje(e, 'No se pudieron cargar los recintos')));
  }, [notify]);

  const config = configs.find(c => c.recinto._id === recintoId);

  const reiniciar = () => {
    setArchivo(null); setFilasArchivo([]); setVista(null); setResultado(null); setFiltro('todas');
    if (inputRef.current) inputRef.current.value = '';
  };

  const plantilla = async () => {
    try {
      await descargarPlantilla({ archivo: 'plantilla-matriculas', columnas: COLUMNAS, instrucciones: INSTRUCCIONES });
    } catch {
      notify.error('No se pudo generar la plantilla');
    }
  };

  const alSubir = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !recintoId) return;
    setResultado(null);
    setProcesando(true);
    try {
      const { filas, faltantes } = await leerPlanilla(file, COLUMNAS);
      if (faltantes.length) throw new Error(`Al archivo le faltan las columnas: ${faltantes.join(', ')}`);
      if (!filas.length) throw new Error('El archivo no trae filas con datos');
      setArchivo(file);
      setFilasArchivo(filas);
      setVista(await MatriculaService.CargaMasiva(recintoId, filas, false));
    } catch (err) {
      notify.error(mensaje(err, 'No se pudo leer el archivo'));
      reiniciar();
    } finally {
      setProcesando(false);
    }
  };

  const confirmar = () => {
    const n = vista?.resumen?.validas || 0;
    Modal.confirm({
      title: `¿Registrar ${n} matrícula${n !== 1 ? 's' : ''}?`,
      content: `Se crean en ${formatearNombre(config?.recinto?.nombre)} sin cobro, como carga masiva. Las filas con error u omitidas no se tocan.`,
      okText: 'Sí, registrar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setProcesando(true);
        try {
          const r = await MatriculaService.CargaMasiva(recintoId, filasArchivo, true);
          setResultado(r);
          setVista(r);
          notify.success(`${r.resumen.creadas} matrícula${r.resumen.creadas !== 1 ? 's' : ''} registrada${r.resumen.creadas !== 1 ? 's' : ''}`);
        } catch (err) {
          notify.error(mensaje(err, 'No se pudo completar la carga'));
        } finally {
          setProcesando(false);
        }
      },
    });
  };

  const filasTabla = useMemo(
    () => (vista?.filas || []).filter(f => filtro === 'todas' || f.estado === filtro),
    [vista, filtro]
  );

  const columnas = [
    { title: 'Fila', dataIndex: 'fila', width: 70, render: n => <span className="tabular-nums text-gray-500">{n}</span> },
    { title: 'RUT', dataIndex: 'rut', width: 130, render: r => <span className="tabular-nums">{r || '—'}</span> },
    { title: 'Nombre', dataIndex: 'nombre', render: n => n || '—' },
    { title: 'Vigencia', dataIndex: 'vigencia', width: 120, render: v => v ? <ChipNeutro>{v}</ChipNeutro> : '—' },
    {
      title: 'Vence', dataIndex: 'venceEl', width: 120,
      render: (v, f) => (f.estado !== 'ok' ? '—' : v ? (
        <span className={f.quedaVencida ? 'text-red-600 font-bold' : ''}>{dayjs(v).format('DD/MM/YYYY')}</span>
      ) : 'No vence'),
    },
    {
      title: 'Resultado', key: 'estado', width: 260,
      render: (_, f) => {
        const e = ESTADO_FILA[f.estado] || ESTADO_FILA.error;
        return (
          <div>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${e.clase}`}>
              {f.estado === 'ok' && f.quedaVencida ? 'Se registra vencida' : e.label}
            </span>
            {f.motivo && <div className="text-xs text-gray-500 mt-1">{f.motivo}</div>}
          </div>
        );
      },
    },
  ];

  const r = vista?.resumen;

  return (
    <div className="max-w-[1280px]">
      <EncabezadoAdmin titulo="Carga masiva de" destacado="matrículas"
        subtitulo="Sube la base de matriculados de un recinto desde un Excel. Primero ves la vista previa; nada se graba hasta que confirmas.">
        <button type="button" onClick={plantilla} className={claseBtnSecundario}>
          <FiDownload size={15} /> Descargar plantilla
        </button>
      </EncabezadoAdmin>

      <div className="flex flex-wrap items-end gap-3 p-4 mb-4 rounded-2xl bg-white border border-gray-100">
        <div className="min-w-[260px]">
          <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="recinto-carga">Recinto</label>
          <Select id="recinto-carga" className="w-full" placeholder="Elige el recinto" value={recintoId}
            onChange={(v) => { setRecintoId(v); reiniciar(); }}
            options={configs.map(c => ({ value: c.recinto._id, label: formatearNombre(c.recinto.nombre) }))} />
        </div>
        <div>
          <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={alSubir} />
          <button type="button" className={claseBtnPrimario} disabled={!recintoId || procesando}
            onClick={() => inputRef.current?.click()}>
            {procesando ? <LoadingOutlined /> : <FiUploadCloud size={15} />} {archivo ? 'Subir otro archivo' : 'Subir archivo'}
          </button>
        </div>
        {archivo && <span className="text-sm text-gray-600">{archivo.name} · {filasArchivo.length} filas</span>}
        {config && !config.activa && (
          <span className="ml-auto text-xs text-gray-500">
            La matrícula de este recinto no está a la venta: se puede cargar la base igual.
          </span>
        )}
      </div>

      {!configs.length && (
        <Alert type="info" showIcon className="mb-4" message="Ningún recinto tiene matrícula configurada"
          description="Primero crea la configuración del recinto en Configuración de Recintos." />
      )}

      {resultado && (
        <Alert type="success" showIcon className="mb-4"
          message={`Carga completada: ${resultado.resumen.creadas} matrícula${resultado.resumen.creadas !== 1 ? 's' : ''} registrada${resultado.resumen.creadas !== 1 ? 's' : ''}`}
          description="Las filas con error u omitidas siguen en la tabla: corrígelas en el Excel y súbelo de nuevo (las ya registradas se omiten)."
          action={<button type="button" onClick={reiniciar} className={claseBtnSecundario}>Nueva carga</button>} />
      )}

      {r && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <TarjetaResumen icon={<FiUsers size={19} />} label="Filas" value={r.total} activa={filtro === 'todas'} onClick={() => setFiltro('todas')} />
            <TarjetaResumen icon={<FiCheckCircle size={19} />} label={resultado ? 'Registradas' : 'Se registran'}
              value={resultado ? r.creadas : r.validas} activa={filtro === 'ok'} onClick={() => setFiltro('ok')} />
            <TarjetaResumen icon={<FiSkipForward size={19} />} label="Se omiten" value={r.omitidas} activa={filtro === 'omitida'} onClick={() => setFiltro('omitida')} />
            <TarjetaResumen icon={<FiAlertTriangle size={19} />} label="Con error" value={r.errores} activa={filtro === 'error'} onClick={() => setFiltro('error')} />
          </div>
          {r.quedanVencidas > 0 && !resultado && (
            <Alert type="warning" showIcon className="mb-4"
              message={`${r.quedanVencidas} se registran como vencidas`}
              description="Su plazo ya se cumplió según la fecha de matrícula. Quedan en la historia de la persona, pero no le dan acceso." />
          )}
          <div className="admin-table-card mb-4">
            <Table columns={columnas} dataSource={filasTabla} rowKey="fila" size="small" scroll={{ x: 900 }}
              pagination={filasTabla.length > 50 ? { pageSize: 50, style: { padding: '12px 16px' } } : false} />
          </div>
          {!resultado && (
            <div className="flex justify-end gap-2">
              <button type="button" onClick={reiniciar} className={claseBtnSecundario}>Cancelar</button>
              <button type="button" onClick={confirmar} disabled={procesando || !r.validas} className={claseBtnPrimario}>
                {procesando && <LoadingOutlined />} Registrar {r.validas} matrícula{r.validas !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CargaMasiva;
